/**
 * Habit Review — one surface, multiple directions.
 *
 * Three tabs over one GET /api/habits/review response:
 * - Adherence: ranked table over closed-period adherence, weakest-first or
 *   strongest-first, every column sortable, filterable by recurrence type
 *   and by habit tag (migration 0020; tag data joined client-side from
 *   GET /api/habits/, degrading to no tag filter when that fetch fails).
 * - Overdue: a schedule signal, not a percentage — habits past one full
 *   recurrence interval since their last completion, sorted by how far past
 *   due, framed as "last done N days ago; the interval is M days".
 * - Streaks: current AND longest streak in a sortable table, each labelled
 *   in the habit's own period unit via formatStreak. A table, not a chart:
 *   periods differ across habits, so only a table can put "12 days" next to
 *   "2 × 46-day cycles" honestly. Shares the tag filter with Adherence.
 *
 * Habits below the ranking floor are never ranked; they share one
 * "not enough history" section (Dashboard's unranked copy, including the
 * can_ever_rank=false wording).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import {
  formatAnalyticsReferenceDateLong,
  getAnalyticsReferenceDateParams,
  useAnalyticsReferenceDate,
} from '../contexts/AnalyticsReferenceDateContext';
import { formatStreak, periodNoun } from '../metrics/streakFormat';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface HabitReviewItem {
  habit_id: number;
  habit_title: string;
  recurrence_type?: string | null;
  recurrence_interval?: number;
  ranking_rate?: number | null;
  closed_periods?: number;
  required_periods?: number;
  can_ever_rank?: boolean;
  rankable?: boolean;
  current_streak: number;
  longest_streak: number;
  last_completed_date?: string | null;
  periods_since_last_completion?: number | null;
  is_overdue?: boolean;
  days_past_due?: number | null;
}

interface HabitReviewResponse {
  reference_date: string;
  items: HabitReviewItem[];
  ranking_min_periods?: number;
  ranking_lookback_days?: number;
}

type ReviewTab = 'adherence' | 'overdue' | 'streaks';
type SortDirection = 'asc' | 'desc';
type AdherenceColumn =
  | 'habit_title'
  | 'recurrence'
  | 'ranking_rate'
  | 'current_streak'
  | 'last_completed_date'
  | 'periods_since_last_completion';
type StreakColumn = 'habit_title' | 'recurrence' | 'current_streak' | 'longest_streak';

const TAB_VALUES: ReviewTab[] = ['adherence', 'overdue', 'streaks'];

/** Whole days between two YYYY-MM-DD dates (b - a), immune to DST. */
const ymdDiffDays = (a: string, b: string): number => {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
};

/**
 * The recurrence interval as honest prose for the overdue framing. Day-based
 * recurrences (daily, weekly, custom) resolve to an exact day count so it can
 * sit next to "last done N days ago"; months and years are calendar units
 * with no fixed day length, so they keep their own words.
 */
const intervalLabel = (recurrenceType?: string | null, interval = 1): string => {
  const n = interval || 1;
  switch (recurrenceType) {
    case 'weekly':
      return `${n * 7} days`;
    case 'monthly':
      return n === 1 ? '1 month' : `${n} months`;
    case 'yearly':
      return n === 1 ? '1 year' : `${n} years`;
    default:
      // daily and custom count in days.
      return n === 1 ? '1 day' : `${n} days`;
  }
};

// "every day", "every week", "every 46-day cycle" — the habit's own unit.
const recurrenceLabel = (item: HabitReviewItem): string =>
  `every ${periodNoun(item.recurrence_type, item.recurrence_interval ?? 1)}`;

const compareNullableNumber = (a?: number | null, b?: number | null): number => {
  // Nulls sort last regardless of direction so "no data" never wins a sort.
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
};

const compareNullableString = (a?: string | null, b?: string | null): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
};

const HabitReview: React.FC = () => {
  const { pageGridSpacing, pageHeaderSx, panelPaddingSx, tableCellPaddingSx } =
    useLayoutTokens();
  const [searchParams] = useSearchParams();
  const {
    asOfDate,
    enabled: analyticsReferenceDateEnabled,
    resetToToday,
  } = useAnalyticsReferenceDate();

  const initialTab = ((): ReviewTab => {
    const requested = searchParams.get('tab');
    return TAB_VALUES.includes(requested as ReviewTab)
      ? (requested as ReviewTab)
      : 'adherence';
  })();
  const initialDirection: SortDirection =
    searchParams.get('dir') === 'strongest' ? 'desc' : 'asc';

  const [tab, setTab] = useState<ReviewTab>(initialTab);
  const [review, setReview] = useState<HabitReviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recurrenceFilter, setRecurrenceFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [habitTags, setHabitTags] = useState<Record<number, string[]>>({});
  const [adherenceSort, setAdherenceSort] = useState<{
    column: AdherenceColumn;
    direction: SortDirection;
  }>({ column: 'ranking_rate', direction: initialDirection });
  const [streakSort, setStreakSort] = useState<{
    column: StreakColumn;
    direction: SortDirection;
  }>({ column: 'current_streak', direction: 'desc' });

  useEffect(() => {
    const fetchReview = async () => {
      try {
        setLoading(true);
        setError(null);
        const tzOffset = new Date().getTimezoneOffset();
        const res = await axios.get(`${API_BASE_URL}/api/habits/review`, {
          params: {
            tz_offset: tzOffset,
            ...getAnalyticsReferenceDateParams(analyticsReferenceDateEnabled, asOfDate),
          },
        });
        setReview(res.data);
      } catch (err) {
        console.error('Failed to fetch habit review:', err);
        setError('Failed to load the habit review. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchReview();
  }, [analyticsReferenceDateEnabled, asOfDate]);

  // The review payload carries no tag data (the endpoint predates migration
  // 0020), so join habit_id → tags from the habit list. A failed fetch
  // degrades to an empty map, which hides the tag filter entirely.
  useEffect(() => {
    const fetchHabitTags = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/habits/`);
        const map: Record<number, string[]> = {};
        if (Array.isArray(res.data)) {
          res.data.forEach((habit: { id?: unknown; tags?: unknown }) => {
            if (typeof habit?.id === 'number' && Array.isArray(habit.tags)) {
              map[habit.id] = habit.tags.filter(
                (tag): tag is string => typeof tag === 'string',
              );
            }
          });
        }
        setHabitTags(map);
      } catch (err) {
        console.error('Failed to fetch habit tags for the review filter:', err);
        setHabitTags({});
      }
    };
    fetchHabitTags();
  }, []);

  const items = useMemo(() => review?.items ?? [], [review]);
  const rankingMinPeriods = review?.ranking_min_periods || 6;

  const filteredItems = useMemo(
    () =>
      recurrenceFilter === 'all'
        ? items
        : items.filter((item) => item.recurrence_type === recurrenceFilter),
    [items, recurrenceFilter],
  );
  const rankableItems = useMemo(
    () => filteredItems.filter((item) => item.rankable),
    [filteredItems],
  );
  // Tag filter (Adherence + Streaks only): the Overdue tab is a schedule
  // alarm and deliberately never hides anything behind a tag.
  const tagFilteredRankable = useMemo(
    () =>
      tagFilter === 'all'
        ? rankableItems
        : rankableItems.filter((item) =>
            (habitTags[item.habit_id] ?? []).includes(tagFilter),
          ),
    [rankableItems, tagFilter, habitTags],
  );
  const tagOptions = useMemo(() => {
    const names = new Set<string>();
    Object.values(habitTags).forEach((tags) => tags.forEach((tag) => names.add(tag)));
    return Array.from(names).sort();
  }, [habitTags]);
  // The not-enough-history section ignores the recurrence filter on purpose:
  // hiding an unmeasured habit because of a filter would just re-bury it.
  const belowFloorItems = useMemo(() => items.filter((item) => !item.rankable), [items]);
  const overdueItems = useMemo(
    () =>
      [...filteredItems]
        .filter((item) => item.is_overdue)
        .sort((a, b) => (b.days_past_due ?? 0) - (a.days_past_due ?? 0)),
    [filteredItems],
  );

  const sortedAdherenceItems = useMemo(() => {
    const { column, direction } = adherenceSort;
    const sign = direction === 'asc' ? 1 : -1;
    return [...tagFilteredRankable].sort((a, b) => {
      let cmp: number;
      switch (column) {
        case 'habit_title':
          cmp = compareNullableString(a.habit_title, b.habit_title);
          break;
        case 'recurrence':
          cmp = compareNullableString(recurrenceLabel(a), recurrenceLabel(b));
          break;
        case 'ranking_rate':
          cmp = compareNullableNumber(a.ranking_rate, b.ranking_rate);
          break;
        case 'current_streak':
          cmp = compareNullableNumber(a.current_streak, b.current_streak);
          break;
        case 'last_completed_date':
          cmp = compareNullableString(a.last_completed_date, b.last_completed_date);
          break;
        default:
          cmp = compareNullableNumber(
            a.periods_since_last_completion,
            b.periods_since_last_completion,
          );
      }
      return cmp !== 0 ? sign * cmp : a.habit_id - b.habit_id;
    });
  }, [tagFilteredRankable, adherenceSort]);

  const sortedStreakItems = useMemo(() => {
    const { column, direction } = streakSort;
    const sign = direction === 'asc' ? 1 : -1;
    return [...tagFilteredRankable].sort((a, b) => {
      let cmp: number;
      switch (column) {
        case 'habit_title':
          cmp = compareNullableString(a.habit_title, b.habit_title);
          break;
        case 'recurrence':
          cmp = compareNullableString(recurrenceLabel(a), recurrenceLabel(b));
          break;
        case 'longest_streak':
          cmp = compareNullableNumber(a.longest_streak, b.longest_streak);
          break;
        default:
          cmp = compareNullableNumber(a.current_streak, b.current_streak);
      }
      return cmp !== 0 ? sign * cmp : a.habit_id - b.habit_id;
    });
  }, [tagFilteredRankable, streakSort]);

  const toggleAdherenceSort = (column: AdherenceColumn) => {
    setAdherenceSort((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleStreakSort = (column: StreakColumn) => {
    setStreakSort((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const streakCell = (item: HabitReviewItem, count: number): string =>
    formatStreak(count, item.recurrence_type, item.recurrence_interval ?? 1);

  const notEnoughHistorySection = belowFloorItems.length > 0 && (
    <Box sx={{ mt: 3 }}>
      {/* Same honesty rules as the Dashboard's unranked list: a habit below
          the ranking floor is unmeasured, not the worst performer — and a
          cycle too long for the lookback window will never rank at all. */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
        Not enough history to rank
      </Typography>
      {belowFloorItems.map((item) => (
        <Typography
          key={item.habit_id}
          variant="caption"
          color="text.secondary"
          display="block"
        >
          {item.habit_title} ·{' '}
          {item.can_ever_rank === false
            ? `cycle too long to rank — fewer than ${
                item.required_periods || rankingMinPeriods
              } cycles fit in a year`
            : `${item.closed_periods ?? 0} of ${
                item.required_periods || rankingMinPeriods
              } cycles closed`}
        </Typography>
      ))}
    </Box>
  );

  const recurrenceFilterControl = (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel id="review-recurrence-filter-label">Recurrence</InputLabel>
      <Select
        labelId="review-recurrence-filter-label"
        label="Recurrence"
        value={recurrenceFilter}
        onChange={(event) => setRecurrenceFilter(event.target.value)}
      >
        <MenuItem value="all">All recurrences</MenuItem>
        <MenuItem value="daily">Daily</MenuItem>
        <MenuItem value="weekly">Weekly</MenuItem>
        <MenuItem value="monthly">Monthly</MenuItem>
        <MenuItem value="yearly">Yearly</MenuItem>
        <MenuItem value="custom">Custom</MenuItem>
      </Select>
    </FormControl>
  );

  // Rendered only when tag data is available — a failed habit fetch (or a
  // user with no tags) simply hides the control instead of offering a dead
  // filter.
  const tagFilterControl = tagOptions.length > 0 && (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel id="review-tag-filter-label">Tag</InputLabel>
      <Select
        labelId="review-tag-filter-label"
        label="Tag"
        value={tagFilter}
        onChange={(event) => setTagFilter(event.target.value)}
      >
        <MenuItem value="all">All tags</MenuItem>
        {tagOptions.map((tag) => (
          <MenuItem key={tag} value={tag}>
            {tag}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (loading) {
    return (
      <PageShell maxWidth="wide">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="wide">
      <Box sx={pageHeaderSx}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }} gutterBottom>
          Habit Review
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ranked adherence, overdue habits, and streaks — each in the habit's own
          period unit.
        </Typography>
      </Box>

      {analyticsReferenceDateEnabled && asOfDate && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={resetToToday}>
              Reset to Today
            </Button>
          }
        >
          Viewing analytics as of {formatAnalyticsReferenceDateLong(asOfDate)}. This does
          not affect habit logging.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={panelPaddingSx}>
        <Tabs
          value={tab}
          onChange={(_event, value: ReviewTab) => setTab(value)}
          sx={{ mb: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Adherence" value="adherence" />
          <Tab label="Overdue" value="overdue" />
          <Tab label="Streaks" value="streaks" />
        </Tabs>

        {tab === 'adherence' && (
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={pageGridSpacing}
              sx={{ mb: 2 }}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={adherenceSort.column === 'ranking_rate' ? adherenceSort.direction : null}
                onChange={(_event, value: SortDirection | null) => {
                  if (value) setAdherenceSort({ column: 'ranking_rate', direction: value });
                }}
                aria-label="ranking direction"
              >
                <ToggleButton value="asc">Weakest first</ToggleButton>
                <ToggleButton value="desc">Strongest first</ToggleButton>
              </ToggleButtonGroup>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                {tagFilterControl}
                {recurrenceFilterControl}
              </Box>
            </Box>
            {sortedAdherenceItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No habits have enough closed periods to rank yet.
              </Typography>
            ) : (
              <TableContainer>
                <Table
                  size="small"
                  aria-label="adherence ranking"
                  sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={adherenceSort.column === 'habit_title' ? adherenceSort.direction : false}>
                        <TableSortLabel
                          active={adherenceSort.column === 'habit_title'}
                          direction={adherenceSort.column === 'habit_title' ? adherenceSort.direction : 'asc'}
                          onClick={() => toggleAdherenceSort('habit_title')}
                        >
                          Habit
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={adherenceSort.column === 'recurrence'}
                          direction={adherenceSort.column === 'recurrence' ? adherenceSort.direction : 'asc'}
                          onClick={() => toggleAdherenceSort('recurrence')}
                        >
                          Recurrence
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={adherenceSort.column === 'ranking_rate'}
                          direction={adherenceSort.column === 'ranking_rate' ? adherenceSort.direction : 'asc'}
                          onClick={() => toggleAdherenceSort('ranking_rate')}
                        >
                          Adherence
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={adherenceSort.column === 'current_streak'}
                          direction={adherenceSort.column === 'current_streak' ? adherenceSort.direction : 'asc'}
                          onClick={() => toggleAdherenceSort('current_streak')}
                        >
                          Current streak
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={adherenceSort.column === 'last_completed_date'}
                          direction={adherenceSort.column === 'last_completed_date' ? adherenceSort.direction : 'asc'}
                          onClick={() => toggleAdherenceSort('last_completed_date')}
                        >
                          Last completed
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={adherenceSort.column === 'periods_since_last_completion'}
                          direction={adherenceSort.column === 'periods_since_last_completion' ? adherenceSort.direction : 'asc'}
                          onClick={() => toggleAdherenceSort('periods_since_last_completion')}
                        >
                          Periods since last
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAdherenceItems.map((item) => (
                      <TableRow key={item.habit_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{item.habit_title}</TableCell>
                        <TableCell>{recurrenceLabel(item)}</TableCell>
                        <TableCell>
                          {item.ranking_rate != null
                            ? `${item.ranking_rate}% over ${item.closed_periods} closed`
                            : '—'}
                        </TableCell>
                        <TableCell>{streakCell(item, item.current_streak)}</TableCell>
                        <TableCell>{item.last_completed_date ?? 'never'}</TableCell>
                        <TableCell>{item.periods_since_last_completion ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {notEnoughHistorySection}
          </Box>
        )}

        {tab === 'overdue' && (
          <Box>
            <Box display="flex" justifyContent="flex-end" sx={{ mb: 2 }}>
              {recurrenceFilterControl}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              More than one full interval has passed since these habits were last
              done — a schedule signal, distinct from low adherence. Sorted by how
              far past due.
            </Typography>
            {overdueItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing is overdue.
              </Typography>
            ) : (
              overdueItems.map((item) => (
                <Box
                  key={item.habit_id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.habit_title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.last_completed_date && review
                        ? `last done ${ymdDiffDays(
                            item.last_completed_date,
                            review.reference_date,
                          )} days ago; the interval is ${intervalLabel(
                            item.recurrence_type,
                            item.recurrence_interval ?? 1,
                          )}`
                        : `never completed; the interval is ${intervalLabel(
                            item.recurrence_type,
                            item.recurrence_interval ?? 1,
                          )}`}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${item.days_past_due} days past due`}
                  />
                </Box>
              ))
            )}
          </Box>
        )}

        {tab === 'streaks' && (
          <Box>
            <Box display="flex" justifyContent="flex-end" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
              {tagFilterControl}
              {recurrenceFilterControl}
            </Box>
            {sortedStreakItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No habits have enough closed periods to rank yet.
              </Typography>
            ) : (
              <TableContainer>
                <Table
                  size="small"
                  aria-label="streaks"
                  sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={streakSort.column === 'habit_title'}
                          direction={streakSort.column === 'habit_title' ? streakSort.direction : 'asc'}
                          onClick={() => toggleStreakSort('habit_title')}
                        >
                          Habit
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={streakSort.column === 'recurrence'}
                          direction={streakSort.column === 'recurrence' ? streakSort.direction : 'asc'}
                          onClick={() => toggleStreakSort('recurrence')}
                        >
                          Recurrence
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={streakSort.column === 'current_streak'}
                          direction={streakSort.column === 'current_streak' ? streakSort.direction : 'desc'}
                          onClick={() => toggleStreakSort('current_streak')}
                        >
                          Current streak
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={streakSort.column === 'longest_streak'}
                          direction={streakSort.column === 'longest_streak' ? streakSort.direction : 'desc'}
                          onClick={() => toggleStreakSort('longest_streak')}
                        >
                          Longest streak
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStreakItems.map((item) => (
                      <TableRow key={item.habit_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{item.habit_title}</TableCell>
                        <TableCell>{recurrenceLabel(item)}</TableCell>
                        <TableCell>{streakCell(item, item.current_streak)}</TableCell>
                        <TableCell>{streakCell(item, item.longest_streak)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {notEnoughHistorySection}
          </Box>
        )}
      </Paper>
    </PageShell>
  );
};

export default HabitReview;
